<script lang="ts">
  /**
   * F8 · The list, drawn.
   *
   * *"Groceries and errands, tablet and both phones, voice entry, live check-off.
   * Resist categories, budgets, history."*
   *
   * Everything this component does not have is in `lib/list.svelte.ts`'s header and
   * is the point of the feature. What it does have is four things and no fifth:
   *
   *  · a box you type into and press Enter,
   *  · a button that listens, when the browser has the ear for it,
   *  · a tick beside every line,
   *  · one button that clears what has been ticked.
   *
   * No sort control, no filter, no count of what it will cost, no "you bought this
   * last week". The measure of this screen is whether somebody standing in a shop
   * can use it one-handed without reading anything, and every control added takes
   * that away from it.
   *
   * ## It says it is not on the phones yet
   *
   * The one thing a shared list must never do is imply a sync that is not
   * happening — somebody ticks off the milk in Woolworths, somebody else buys milk.
   * There is no list route in `fabric-api` and the Front Door has no server, so the
   * store is local to this device and the panel says so in one quiet line with the
   * next action beside it. See `lib/list.svelte.ts` for the two ways to make it
   * true and why one of them was rejected.
   */
  import { Icon, Panel } from "@steyncd/steyn-design";
  import { list, voice, SHARING, VOICE_AVAILABLE } from "../lib/list.svelte";

  let {
    /**
     * The kitchen tablet. Bigger targets — the type is already re-based on that
     * screen's root, so only the things a wet hand has to hit change here. Named
     * for the surface rather than for a density, because on this component "wall"
     * means *larger*, and a prop called `dense` that made things bigger would be
     * read wrongly by whoever touches it next.
     */
    wall = false,
  }: { wall?: boolean } = $props();

  let draft = $state("");
  let box = $state<HTMLInputElement | null>(null);

  function submit(e: SubmitEvent) {
    e.preventDefault();
    // A rejected duplicate clears the box anyway. The item is already on the list,
    // which is the outcome the person wanted; leaving their typing in place would
    // read as a failure and invite them to press Add again.
    list.add(draft);
    draft = "";
    box?.focus();
  }

  /**
   * Voice goes straight onto the list rather than into the box.
   *
   * Confirming a transcription would double the number of actions for the one entry
   * path that exists because your hands are full. A wrong item is one tap to delete,
   * which is cheaper than a confirm step on every correct one.
   */
  function speak() {
    if (voice.listening) {
      voice.stop();
      return;
    }
    voice.start((text) => list.add(text));
  }
</script>

<div class="list" class:list--wall={wall}>
  <form class="add" onsubmit={submit}>
    <!-- `aria-label` rather than a `<label for>`: this component renders on Home and
         on the kitchen tablet, and a hard-coded `id` in a component that can appear
         twice is a duplicate id waiting to happen. The placeholder is not doing the
         labelling — a placeholder disappears the moment somebody types. -->
    <input
      class="add__box"
      aria-label="Add to the list"
      bind:this={box}
      bind:value={draft}
      type="text"
      autocomplete="off"
      enterkeyhint="done"
      placeholder="Milk, dog food, fetch the parcel…"
    />
    <button class="add__go" type="submit" disabled={draft.trim() === ""}>Add</button>
    {#if VOICE_AVAILABLE}
      <!-- No glyph on this button, deliberately. `Icon.svelte` has no microphone and
           the estate's rule is that a missing glyph is added to that map rather than
           drawn locally — that map lives in another repo, so the button carries the
           word alone until it does. A word with no glyph is a smaller failure than
           an inlined SVG, and much smaller than borrowing `chat` for a microphone. -->
      <button class="add__voice" type="button" onclick={speak} aria-pressed={voice.listening}>
        {voice.listening ? "Stop" : "Speak"}
      </button>
    {/if}
  </form>

  {#if voice.listening}
    <!-- Glyph, word and colour, like every other state in the estate. Amber because
         it wants something from you — the room is being listened to. -->
    <p class="mark"><Icon name="dot" size={13} /> Listening — say one thing</p>
  {/if}
  {#if voice.note}
    <p class="note">{voice.note}</p>
  {/if}

  <Panel isEmpty={list.items.length === 0}>
    {#snippet empty()}
      Nothing on the list.
      <span class="next">
        Type what you need and press Enter{VOICE_AVAILABLE ? ", or press Speak" : ""}. Anyone at
        this screen can add to it — it never asks who you are.
      </span>
    {/snippet}

    <ul class="rows">
      {#each list.open as i (i.id)}
        <li class="row">
          <label class="row__tick">
            <input type="checkbox" checked={i.done} onchange={() => list.toggle(i.id)} />
            <span class="row__txt">{i.text}</span>
          </label>
          <button class="row__x" onclick={() => list.remove(i.id)} aria-label="Remove {i.text}">
            <Icon name="x" size={14} />
          </button>
        </li>
      {/each}

      {#each list.done as i (i.id)}
        <!-- Ticked items stay put until somebody clears them, struck through and
             quiet. Not an archive — see `lib/list.svelte.ts`. They are here so a
             mis-tap is visible and reversible while you are still in the aisle. -->
        <li class="row row--done">
          <label class="row__tick">
            <input type="checkbox" checked={i.done} onchange={() => list.toggle(i.id)} />
            <span class="row__txt">{i.text}</span>
          </label>
          <button class="row__x" onclick={() => list.remove(i.id)} aria-label="Remove {i.text}">
            <Icon name="x" size={14} />
          </button>
        </li>
      {/each}
    </ul>

    {#if list.done.length > 0}
      <button class="clear" onclick={() => list.clearDone()}>
        Clear {list.done.length} ticked
      </button>
    {/if}
  </Panel>

  {#if !list.persists}
    <!-- The browser refused storage. Said out loud, because the failure is otherwise
         invisible until a reload eats the list. -->
    <p class="mark"><Icon name="alert" size={13} /> This browser will not keep the list. It will empty on reload.</p>
  {/if}

  <p class="slot">
    <span class="slot__l">{SHARING.title}</span>
    — {SHARING.why}
    <span class="next">{SHARING.next}</span>
  </p>
</div>

<style>
  .list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  /* ── Adding ─────────────────────────────────────────────────────────────── */
  .add {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .add__box {
    flex: 1 1 180px;
    min-width: 0;
    min-height: 44px;
    padding: 0 12px;
    border: 1px solid var(--line);
    border-radius: var(--r-control, 10px);
    background: var(--s2);
    color: var(--tx);
    font-size: var(--fs-body);
  }
  .add__box::placeholder {
    color: var(--mut);
  }
  .add__go,
  .add__voice {
    min-height: 44px;
    padding: 0 15px;
    border: 1px solid var(--line);
    border-radius: var(--r-control, 10px);
    font-size: var(--fs-small);
    font-weight: 800;
    color: var(--tx);
    cursor: pointer;
  }
  /* Copper on the one control that does the thing the panel is for. The estate's
     rule is that copper appears only on things you can act on. */
  .add__go {
    border-color: var(--acc);
    color: var(--acc);
  }
  .add__go:disabled {
    border-color: var(--line);
    color: var(--mut);
    cursor: default;
  }

  /* ── Rows ───────────────────────────────────────────────────────────────── */
  .rows {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
    border-bottom: 1px solid var(--line);
  }
  .row:last-child {
    border-bottom: none;
  }
  /* The whole line is the target, not just the box. One-handed, in a shop. */
  .row__tick {
    display: flex;
    align-items: center;
    gap: 11px;
    flex: 1;
    min-width: 0;
    min-height: 44px;
    cursor: pointer;
  }
  .row__tick input {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    /* An existing token, and the only colour on the control. The tick's own shape
       and the strike-through below carry the state without it. */
    accent-color: var(--acc);
  }
  .row__txt {
    font-size: var(--fs-body);
    line-height: 1.4;
    color: var(--tx);
    overflow-wrap: anywhere;
  }
  /* Done: struck through AND muted AND at the bottom of the list. Three signals,
     none of them a colour on its own. */
  .row--done .row__txt {
    text-decoration: line-through;
    color: var(--mut);
  }
  .row__x {
    padding: 6px;
    border-radius: var(--r-control, 10px);
    color: var(--mut);
    flex-shrink: 0;
    cursor: pointer;
  }
  .row__x:hover {
    color: var(--acc);
  }

  .clear {
    align-self: flex-start;
    margin-top: 8px;
    min-height: 44px;
    padding: 0 14px;
    border: 1px solid var(--line);
    border-radius: var(--r-pill);
    font-size: var(--fs-micro);
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--tx2);
    cursor: pointer;
  }
  .clear:hover {
    color: var(--acc);
    border-color: var(--acc);
  }

  /* ── States and the named gap ───────────────────────────────────────────── */
  .mark {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin: 0;
    font-size: var(--fs-micro);
    font-weight: 800;
    color: var(--warn);
  }
  .note {
    margin: 0;
    font-size: var(--fs-micro);
    color: var(--tx2);
  }
  .slot {
    margin: 0;
    font-size: var(--fs-micro);
    line-height: 1.45;
    color: var(--mut);
  }
  .slot__l {
    font-weight: 800;
    color: var(--tx2);
  }
  .next {
    display: block;
    margin-top: 5px;
    font-weight: 700;
    color: var(--tx2);
  }

  /* The kitchen tablet. Read from two metres and tapped with a wet hand, so the
     targets grow — the type is already re-based on that screen's root and needs
     nothing here. */
  .list--wall .row__tick {
    min-height: 52px;
  }
  .list--wall .row__tick input {
    width: 26px;
    height: 26px;
  }
</style>
